// ─────────────────────────────────────────────────────────────────────────────
// Sidebar Component - TypeScript Implementation
// Navigation sidebar with collapsible design and routing integration
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuthState, useAuthActions } from "../../hooks/providers/AuthProvider";
import LogoutConfirmationModal from "../Modals/LogoutConfirmationModal";
import SyncDebugModal from "../Debug/SyncDebugModal";
import "./Sidebar.css";

// Menu item interface
interface MenuItem {
  path: string;
  icon: string;
  label: string;
  end?: boolean;
}

// User information interface
interface UserInfo {
  name: string;
  role: string;
  avatar?: string;
}

// Sidebar component props (if needed for future extensions)
interface SidebarProps {
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  className = "",
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true); // Default to closed
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugClickCount, setDebugClickCount] = useState(0);
  const [debugClickTimer, setDebugClickTimer] = useState<NodeJS.Timeout | null>(null);
  const authState = useAuthState();
  const authActions = useAuthActions();
  const location = useLocation();
  
  // Function to check if current route should make "Sedations" tab active
  const isSedationRoute = (pathname: string): boolean => {
    const sedationRoutes = [
      '/sedation-workflow',
      '/sedations',
      '/patients/edit/', // Patient editing goes through sedation workflow
      '/pre-sedation',
      '/intra-sedation',
      '/post-sedation',
      '/sedation-list'
    ];
    
    return sedationRoutes.some(route => pathname.startsWith(route));
  };
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debugClickTimer) {
        clearTimeout(debugClickTimer);
      }
    };
  }, [debugClickTimer]);
  
  // Get user information from auth state
  const userInfo: UserInfo = {
    name: authState?.userProfile?.firstName && authState?.userProfile?.lastName
      ? `${authState.userProfile.firstName} ${authState.userProfile.lastName}`
      : authState?.userProfile?.email || "Unknown User",
    role: authState?.userProfile?.roles?.[0] || "User",
    avatar: "👤"
  };

  const toggleSidebar = (): void => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    authActions.handleLogoutActionClick();
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Hidden debug modal access - 5 clicks within 3 seconds on user avatar
  const handleUserAvatarClick = () => {
    const newCount = debugClickCount + 1;
    setDebugClickCount(newCount);

    // Clear existing timer
    if (debugClickTimer) {
      clearTimeout(debugClickTimer);
    }

    // Check if we've reached 5 clicks
    if (newCount >= 5) {
      setShowDebugModal(true);
      setDebugClickCount(0);
      setDebugClickTimer(null);
      return;
    }

    // Set timer to reset count after 3 seconds
    const timer = setTimeout(() => {
      setDebugClickCount(0);
      setDebugClickTimer(null);
    }, 3000);

    setDebugClickTimer(timer);
  };

  const handleCloseDebugModal = () => {
    setShowDebugModal(false);
  };

  const menuItems: MenuItem[] = [
    { path: "/", icon: "📋", label: "Info", end: true },
    { path: "/patients", icon: "👥", label: "Patients", end: true }, // Only match exact /patients path
    { path: "/sedation-workflow", icon: "💉", label: "Sedations" }, // This will use custom logic
    { path: "/tips-and-tutorials", icon: "🎓", label: "Tips & Tutorials" }, // S-20: from the Hub's Knowledge Hub
  ];

  const bottomMenuItem: MenuItem = {
    path: "#", // Change to # to prevent navigation since we'll handle it with onClick
    icon: "🚪",
    label: "Logout",
  };

  return (
    <div className={`sidebar ${isCollapsed ? "collapsed" : ""} ${className}`}>
      <div className="sidebar-header">
        <div className="logo-container">
          {isCollapsed ? (
            <img
              src={`${process.env.PUBLIC_URL}/images/monogram-white.png`}
              alt="Sedation Solutions"
              className="sidebar-monogram"
            />
          ) : (
            <div className="company-info">
              {/* Wordmark when there is room; the square mark stays for the 48px collapsed bar */}
              <img
                src={`${process.env.PUBLIC_URL}/images/wordmark-white.png`}
                alt="Sedation Solutions"
                className="sidebar-wordmark"
              />
              <p className="company-tagline">Built for Sedation</p>
              <p className="company-tagline">Nothing Else</p>
            </div>
          )}
        </div>
      </div>

      <div className="toggle-section">
        <button
          className="toggle-btn"
          onClick={toggleSidebar}
          type="button"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          <span className="toggle-icon">{isCollapsed ? "›" : "‹"}</span>
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {menuItems.map((item) => (
            <li key={item.path} className="nav-item">
              <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }: { isActive: boolean }) => {
                  // For sedations tab, use custom logic to determine if it should be active
                  if (item.path === '/sedations') {
                    const shouldBeActive = isSedationRoute(location.pathname);
                    return `nav-link ${shouldBeActive ? "active" : ""}`;
                  }
                  // Default behavior for other tabs
                  return `nav-link ${isActive ? "active" : ""}`;
                }}
                title={isCollapsed ? item.label : ""}
                aria-label={item.label}
              >
                <span className="nav-icon" role="img" aria-label={item.label}>
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <span className="nav-label">{item.label}</span>
                )}
                {!isCollapsed && <span className="nav-arrow">&#8250;</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <div className="logout-section">
          <button
            onClick={handleLogout}
            className="nav-link logout-link"
            title={isCollapsed ? bottomMenuItem.label : ""}
            aria-label={bottomMenuItem.label}
            style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
          >
            <span
              className="nav-icon"
              role="img"
              aria-label={bottomMenuItem.label}
            >
              {bottomMenuItem.icon}
            </span>
            {!isCollapsed && (
              <span className="nav-label">{bottomMenuItem.label}</span>
            )}
            {!isCollapsed && <span className="nav-arrow">&#8250;</span>}
          </button>
        </div>

        {!isCollapsed && (
          <div className="user-info">
            <div 
              className="user-avatar" 
              role="img" 
              aria-label="User avatar"
              onClick={handleUserAvatarClick}
              style={{ cursor: 'pointer' }}
              title={`${userInfo.name} - ${userInfo.role}`}
            >
              {userInfo.avatar || "👤"}
            </div>
            <div className="user-details">
              <p className="user-name">{userInfo.name}</p>
              <p className="user-role">{userInfo.role}</p>
            </div>
          </div>
        )}
        {isCollapsed && (
          <div
            className="user-avatar-collapsed"
            role="img"
            aria-label="User avatar"
            title={`${userInfo.name} - ${userInfo.role}`}
            onClick={handleUserAvatarClick}
            style={{ cursor: 'pointer' }}
          >
            {userInfo.avatar || "👤"}
          </div>
        )}
      </div>

      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        userName={userInfo.name}
      />
      
      <SyncDebugModal
        isOpen={showDebugModal}
        onClose={handleCloseDebugModal}
      />
    </div>
  );
};

export default Sidebar;
export type { SidebarProps, MenuItem, UserInfo };
